import React, { useEffect, useMemo, useState } from 'react';
import { marketingService } from '../../services/marketingService';
import './MarketingPages.css';

interface MarketingPage {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image: string;
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  cta_text: string;
  cta_link: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const deriveTitleFromSlug = (slug: string) => {
  if (!slug) return 'Ny sida';
  return slug
    .split(/[-_]/g)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const normaliseSlug = (slug: string) => slug.replace(/^\/+/, '').replace(/\/+$/, '');

const MarketingPages: React.FC = () => {
  const [pages, setPages] = useState<MarketingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [bulkInput, setBulkInput] = useState('');
  const [importing, setImporting] = useState(false);

  const sortedPages = useMemo(
    () => [...pages].sort((a, b) => a.slug.localeCompare(b.slug)),
    [pages]
  );

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data: MarketingPage[] = await marketingService.getAll();
      setPages(data);
      if (data.length) {
        const lines = data
          .sort((a: MarketingPage, b: MarketingPage) => a.slug.localeCompare(b.slug))
          .map((page: MarketingPage) => {
            const marketingUrl = `https://lillaflora.nu/${page.slug}`;
            const title = page.title || deriveTitleFromSlug(page.slug);
            return `${title} | ${marketingUrl}`;
          })
          .join('\n');
        setBulkInput(lines);
      } else {
        setBulkInput('');
      }
    } catch (err) {
      console.error('Error loading marketing pages:', err);
      setError('Kunde inte ladda marknadsföringssidor');
    } finally {
      setLoading(false);
    }
  };

  const parseLine = (line: string) => {
    if (!line) return null;
    let rawTitle = '';
    let rawUrl = line;

    if (line.includes('|')) {
      const [titlePart, urlPart] = line.split('|');
      rawTitle = titlePart.trim();
      rawUrl = urlPart.trim();
    } else if (line.includes(',')) {
      const [titlePart, urlPart] = line.split(',');
      rawTitle = titlePart.trim();
      rawUrl = urlPart.trim();
    }

    let slug = '';
    let url = rawUrl;

    if (rawUrl.startsWith('http')) {
      try {
        const parsed = new URL(rawUrl);
        const parts = parsed.pathname.split('/').filter(Boolean);
        slug = parts.pop() || '';
        url = parsed.href;
      } catch (err) {
        console.warn('Ogiltig URL, tolkar som slug:', rawUrl);
        slug = normaliseSlug(rawUrl);
        url = `https://lillaflora.nu/${slug}`;
      }
    } else if (rawUrl.startsWith('/')) {
      slug = normaliseSlug(rawUrl);
      url = `https://lillaflora.nu/${slug}`;
    } else {
      slug = normaliseSlug(rawUrl);
      url = `https://lillaflora.nu/${slug}`;
    }

    slug = normaliseSlug(slug);
    if (!slug) return null;

    const title = rawTitle || deriveTitleFromSlug(slug);

    return { slug, url, title };
  };

  const handleBulkImport = async () => {
    setImporting(true);
    setStatus(null);
    setError(null);
    try {
      const lines = bulkInput
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean);

      if (!lines.length) {
        setError('Klistra in minst en rad innan du sparar.');
        return;
      }

      const existingSlugs = new Set(pages.map(page => page.slug));
      let created = 0;
      let skipped = 0;

      for (const line of lines) {
        const parsed = parseLine(line);
        if (!parsed) {
          skipped += 1;
          continue;
        }

        if (existingSlugs.has(parsed.slug)) {
          skipped += 1;
          continue;
        }

        try {
          await marketingService.create({
            title: parsed.title,
            slug: parsed.slug,
            excerpt: '',
            content: '',
            featured_image: '',
            seo_title: parsed.title,
            seo_description: '',
            seo_keywords: '',
            cta_text: 'Visa sida',
            cta_link: parsed.url,
            is_active: false
          });
          created += 1;
        } catch (err) {
          console.error('Kunde inte skapa marknadsföringssida', parsed.slug, err);
        }
      }

      await loadPages();

      if (created > 0) {
        setStatus(`Importerade ${created} sid${created === 1 ? 'a' : 'or'}.`);
      }
      if (skipped > 0) {
        setStatus(prev =>
          prev
            ? `${prev} ${skipped} rad${skipped === 1 ? '' : 'er'} hoppades över (redan registrerade eller ogiltiga).`
            : `${skipped} rad${skipped === 1 ? '' : 'er'} hoppades över (redan registrerade eller ogiltiga).`
        );
      }
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna rad från listan?')) {
      return;
    }

    try {
      await marketingService.delete(id);
      setStatus('Sidan togs bort från listan.');
      await loadPages();
    } catch (err) {
      console.error('Error deleting marketing page:', err);
      setError('Kunde inte ta bort sidan');
    }
  };

  if (loading) {
    return <div className="admin-loading">Laddar marknadsföringssidor...</div>;
  }

  return (
    <div className="marketing-pages-admin">
      <div className="admin-header">
        <h2>Marknadsföringssidor</h2>
        <p>Klistra in en lista med SEO-sidor du vill följa. Format: <code>Titel | URL</code> eller bara URL/slug.</p>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {status && <div className="admin-success">{status}</div>}

      <section className="marketing-bulk-import">
        <label htmlFor="bulk-input">Lista över sidor</label>
        <textarea
          id="bulk-input"
          rows={12}
          value={bulkInput}
          onChange={(e) => setBulkInput(e.target.value)}
          placeholder={`Exempel:\nhttps://lillaflora.nu/produkter?kategori=ljus\nMajas Cottage | https://lillaflora.nu/majas-cottage-svenska-inredningsdetaljer`}
        />
        <div className="marketing-actions">
          <button
            className="marketing-primary"
            onClick={handleBulkImport}
            disabled={importing}
          >
            {importing ? 'Sparar…' : 'Spara lista'}
          </button>
          <button
            type="button"
            className="marketing-secondary"
            onClick={loadPages}
            disabled={importing}
          >
            Återställ från databas
          </button>
        </div>
        <p className="marketing-help">
          Varje rad kan innehålla en URL eller slug. Du kan även skriva <code>Titel | URL</code> för att ange ett eget namn.
          Nya rader sparas som inaktiva och påverkar inte publika sidor.
        </p>
      </section>

      <section className="marketing-pages-table">
        <h3>Registrerade sidor</h3>
        {sortedPages.length === 0 ? (
          <div className="marketing-empty-state">
            <p>Inga sidor registrerade ännu. Klistra in din lista ovan för att börja.</p>
          </div>
        ) : (
          <div className="marketing-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Titel</th>
                  <th>Slug</th>
                  <th>Aktiv</th>
                  <th>Uppdaterad</th>
                  <th>Åtgärder</th>
                </tr>
              </thead>
              <tbody>
                {sortedPages.map((page: MarketingPage) => {
                  const title = page.title || deriveTitleFromSlug(page.slug);
                  const url = `https://lillaflora.nu/${page.slug}`;
                  const updated = page.updated_at
                    ? new Date(page.updated_at).toLocaleDateString('sv-SE')
                    : '';
                  return (
                    <tr key={page.id}>
                      <td>{title}</td>
                      <td>{page.slug}</td>
                      <td>{page.is_active ? 'Ja' : 'Nej'}</td>
                      <td>{updated}</td>
                      <td className="marketing-table-actions">
                        <a href={url} target="_blank" rel="noopener noreferrer" className="marketing-link">
                          Visa
                        </a>
                        <button
                          type="button"
                          className="marketing-delete"
                          onClick={() => handleDelete(page.id)}
                        >
                          Ta bort
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default MarketingPages;
